/**
 * Normalize Firestore Timestamps, plain {seconds}, Admin JSON {_seconds}, ISO strings, ms/s numbers → Date.
 */
export function toJsDate(value: unknown): Date | null {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'object' && value !== null) {
    const v = value as Record<string, unknown>;
    if (typeof v.toDate === 'function') {
      try {
        const d = (v.toDate as () => Date)();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
      } catch {
        return null;
      }
    }

    const secRaw = v.seconds ?? v._seconds;
    const sec = typeof secRaw === 'number' ? secRaw : typeof secRaw === 'string' ? Number(secRaw) : NaN;
    if (Number.isFinite(sec)) {
      const nanoRaw = v.nanoseconds ?? v._nanoseconds ?? 0;
      const nano =
        typeof nanoRaw === 'number' ? nanoRaw : typeof nanoRaw === 'string' ? Number(nanoRaw) : 0;
      const d = new Date(sec * 1000 + (Number.isFinite(nano) ? nano : 0) / 1e6);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  if (typeof value === 'string') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return new Date(parsed);
    return null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return null;
}

export function formatDisplayDate(
  value: unknown,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  emptyLabel = '—'
): string {
  const d = toJsDate(value);
  if (!d) return emptyLabel;
  try {
    return new Intl.DateTimeFormat(undefined, options).format(d);
  } catch {
    return emptyLabel;
  }
}
